import { useLazyQuery } from "@apollo/client/react";
import { useCallback, useMemo } from "react";
import { useStore } from "@/app/store";
import { FORMULA_TEMPLATES } from "../graphql/queries";

export interface FormulaTemplateOption {
  id: string;
  name: string;
  expression: string;
  requiredVariableIds: string[];
}

export const useFormulaTemplatesPrefetch = () => {
  const [fetchTemplates] = useLazyQuery(FORMULA_TEMPLATES);

  return useCallback(
    (subCategoryId: string) => {
      if (useStore.getState().formulaTemplatesBySubCategory[subCategoryId])
        return;
      const storeError = (e: unknown) =>
        useStore
          .getState()
          .setFormulaTemplatesError(
            subCategoryId,
            e instanceof Error ? e : new Error("Failed to load templates"),
          );

      void fetchTemplates({ variables: { subCategoryId } })
        .then((res) => {
          if (res.error) {
            storeError(res.error);
            return;
          }
          useStore
            .getState()
            .setFormulaTemplates(
              subCategoryId,
              res.data?.formulaTemplates ?? [],
            );
        })
        .catch(storeError);
    },
    [fetchTemplates],
  );
};

const useFormulaTemplates = (subCategoryId: string | null) => {
  const cached = useStore((s) =>
    subCategoryId ? s.formulaTemplatesBySubCategory[subCategoryId] : undefined,
  );
  const error = useStore((s) =>
    subCategoryId
      ? s.formulaTemplatesErrorBySubCategory[subCategoryId]
      : undefined,
  );

  const templates = useMemo<FormulaTemplateOption[]>(
    () => cached ?? [],
    [cached],
  );

  return {
    templates,
    loading: cached === undefined && error === undefined,
    error,
  };
};

export default useFormulaTemplates;
